// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import styled from 'styled-components';
import { useOpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import { getSafeCashPaymentOption } from '@tectonics/prs/incoming-payment/payment-option';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { RadioButton } from '../../../../../atoms';
import { InformationBox } from '../../../../../organisms/information-box';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { getSafeBankIconSrc } from './get-bank-icon-src';
import { Empty } from '@tectonics/components';
import { getSafePaymentOptionsV2 } from '@tectonics/prs/incoming-payment/get-payment-options';

export function FieldArgentineCashBankList(): Node {
  const [selectedBankCode, setSelectedBankCode] = useUserInputValue('selectedBankCode');
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('selectedBankCode');
  const submitting = useSubmitting();
  const inputId = useUniqueId();

  const incomingPayment = useOpenedIncomingPayment();
  const paymentOptions = getSafePaymentOptionsV2(incomingPayment);
  const bankList = getSafeCashPaymentOption(paymentOptions);

  return (
    <div data-test-name="payment-type-code">
      <Title>Selecciona el método de pago</Title>

      {bankList.map(({ name, value }) => (
        <Label key={value} htmlFor={`${inputId}-${value}`}>
          <RadioButton
            id={`${inputId}-${value}`}
            name="payment_type_code"
            value={value}
            checked={value === selectedBankCode}
            onChange={(e) => setSelectedBankCode(e.target.value)}
            disabled={submitting}
            data-error={formHasBeenSent && validationError ? 'error' : null}
            data-test-value={value}
          />
          <BankIcon src={getSafeBankIconSrc(value)} alt={value} />
          <Name>{name}</Name>
        </Label>
      ))}

      <BankCodeTips bankCode={selectedBankCode}/>

      {formHasBeenSent && validationError && (
        <ValidationErrorMessage validationError={validationError} />
      )}
    </div>
  );
}

const Title = styled.h1`
  font-size: 1em;
  color: ${props => props.theme.colors.secondary(30)};
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-top: 16px;
`;

const BankIcon = styled.img`
  height: 24px;
  width: auto;
  margin-left: 12px;
`;

const Name = styled.span`
  margin-left: 12px;
`;

type ValidationErrorMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          Selecciona el método de pago
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return <ErrorLabel>Invalido</ErrorLabel>;
  }
}

type BankCodeTipsProps = {|
  bankCode: string,
|};

function BankCodeTips({ bankCode }: BankCodeTipsProps): Node {
  switch (bankCode) {
    case 'cupon':
      return (
        <div style={{ margin: '20px' }} data-test-id="cupom-hint-message">
          <InformationBox>
            <strong>Locales de pago disponibles:</strong> Rapipago, PagoFacil, CobroExpress, BancoPagos, ProvinciaPagos, ProntoPago, FormoPagos, PagoListo, PampaPagos o ChubutPagos.
          </InformationBox>
        </div>
      );

    default:
      return <Empty />;
  }
}
